<template>
  <div>
    <ul>
      <li
        v-for="otherCheckbox in otherCheckboxes"
        :key="otherCheckbox.category"
        class="list-items"
      >
        <ApplicationListCheck
          :hide-icon="true"
          :is-disabled="isIncoming || isRemoved"
          :is-checked="checkedFiles.includes(otherCheckbox.category)"
          :icon-url="getCheckboxIconUrl(otherCheckbox.category)"
          :comment="null"
          :has-files="false"
          :icon-title="getCheckboxTitle(otherCheckbox.category)"
          @checked="
            $emit('checksChange', {
              checked: $event,
              category: otherCheckbox.category
            })
          "
          >{{ getLabel(otherCheckbox.category) }}: {{ otherCheckbox.value }}
        </ApplicationListCheck>
      </li>
      <li
        v-for="category in categoriesForType"
        :key="getCategoryId(category)"
        class="list-items"
      >
        <ApplicationListCheck
          :hide-icon="!isIncoming"
          :is-disabled="isIncoming || isRemoved"
          :is-checked="checkedFiles.includes(getCategoryId(category))"
          :icon-url="getCheckboxIconUrl(category)"
          :comment="getComment(category)"
          :has-files="!isIncoming && fileExists(category)"
          :icon-title="getCheckboxTitle(category)"
          @checked="
            $emit('checksChange', {
              checked: $event,
              category: getCategoryId(category)
            })
          "
          @selectedComment="
            $emit('selectComment', {
              content: $event.content,
              documentType: $event.documentType,
              isTransferor
            })
          "
          @openFile="
            $emit('openFile', {
              index: 0,
              category,
              categoryType: activeCategoryType
            })
          "
          >{{ getLabel(category) }}
        </ApplicationListCheck>
      </li>
      <li
        v-for="category in currentAttachmentsCategories"
        :key="getCategoryId(category)"
        class="list-items"
      >
        <ApplicationListCheck
          :hide-icon="!isIncoming"
          :is-disabled="isIncoming || isRemoved"
          :is-checked="checkedFiles.includes(getCategoryId(category))"
          :icon-url="getCheckboxIconUrl(category)"
          :has-files="!isIncoming"
          :icon-title="getCheckboxTitle(category)"
          @checked="
            $emit('checksChange', {
              checked: $event,
              category: getCategoryId(category)
            })
          "
          @selectedComment="
            $emit('selectComment', {
              content: $event.content,
              documentType: $event.documentType,
              isTransferor
            })
          "
          @openFile="
            $emit('openFile', {
              index: 0,
              category,
              categoryType: activeCategoryType
            })
          "
          >{{ getLabel(category) }}
        </ApplicationListCheck>
      </li>
    </ul>
  </div>
</template>

<script>
import svgs from '@/utils/icon-constants';
import docTypeUtils from '@/utils/docs-utils';
import ApplicationListCheck from '@/components/ApplicationListCheck.vue';

export default {
  components: {
    ApplicationListCheck
  },
  props: {
    application: {
      type: Object,
      required: true
    },
    files: {
      type: Array,
      required: true
    },
    checkedFiles: {
      type: Array,
      required: true
    },
    otherCheckboxes: {
      type: Array,
      default: () => []
    },
    applicationStatus: {
      type: String,
      default: null
    },
    isTransferor: {
      type: Boolean,
      default: true
    },
    attachments: {
      type: Array,
      default: () => []
    }
  },
  emits: ['checksChange', 'openFile', 'selectComment'],

  data() {
    return {
      svgs,
      svCollator: new Intl.Collator('sv', {
        sensitivity: 'accent',
        numeric: true,
        ignorePunctuation: true
      })
    };
  },
  computed: {
    currentAttachmentsCategories() {
      const party = this.isTransferor
        ? this.application.currentTenants?.[0]
        : this.application.secondaryTenants?.[0];
      if (!party?.isAB) {
        return [];
      }
      const expectedFolder = this.isTransferor ? 'transferor' : 'transferee';
      const hasKreditUpplysning = this.attachments.find(
        file =>
          file.category === 'kreditupplysning' && file.folder === expectedFolder
      );
      return hasKreditUpplysning ? ['kreditupplysning'] : [];
    },
    adminNotes() {
      return this.application.adminNotes;
    },
    categoriesForType() {
      const categories = [
        ...this.requiredDocumentsForType,
        ...this.noneRequiredDocumentsForType
      ];

      return categories?.length
        ? categories.sort((a = '', b = '') =>
            this.svCollator.compare(a.normalize('NFC'), b.normalize('NFC'))
          )
        : categories;
    },
    requiredDocumentsForType(state) {
      if (!this.isTransferor) {
        if (!state.application?.inviteeComputedDocumentsGroups) {
          return [];
        }
        return Object.entries(state.application.inviteeComputedDocumentsGroups)
          .filter(([, props]) => props.required === 1)
          .map(item => item[0]);
      }
      if (!state.application?.computedDocumentsGroups) {
        return [];
      }
      return Object.entries(state.application.computedDocumentsGroups)
        .filter(([, props]) => props.required === 1)
        .map(item => item[0]);
    },
    noneRequiredDocumentsForType(state) {
      if (!this.isTransferor) {
        if (!state.application?.inviteeComputedDocumentsGroups) {
          return [];
        }
        return Object.entries(state.application.inviteeComputedDocumentsGroups)
          .filter(([, props]) => props.required === 0)
          .map(item => item[0]);
      }
      if (!state.application?.computedDocumentsGroups) {
        return [];
      }
      return Object.entries(state.application.computedDocumentsGroups)
        .filter(([, props]) => props.required === 0)
        .map(item => item[0]);
    },
    isIncoming() {
      return this.applicationStatus === 'INCOMING';
    },
    isRemoved() {
      return this.applicationStatus === 'REMOVED';
    },
    activeFiles() {
      return this.files.filter(
        file => file.categoryType === this.activeCategoryType
      );
    },
    activeCategoryType() {
      if (!this.isTransferor) {
        return 'transferee';
      }
      return 'transferor';
    }
  },
  methods: {
    getComment(category) {
      if (!this.adminNotes[this.activeCategoryType]) {
        return null;
      }

      const note = this.adminNotes[this.activeCategoryType].find(
        note => note.category === category
      );

      if (!note) {
        return null;
      }
      return {
        content: note.note,
        documentType: category
      };
    },
    getCategoryId(category) {
      return `${category}-${this.isTransferor ? 'transferor' : 'transferee'}`;
    },
    fileExists(category) {
      return this.activeFiles.some(file => file.type === category);
    },
    getCheckboxIconUrl(category) {
      const fileExist = this.fileExists(category);
      if (fileExist) {
        return this.svgs.ICONS.CHECK;
      }
      return this.svgs.ICONS.YELLOW_CLOCK;
    },
    getCheckboxTitle(category) {
      if (this.getComment(category)) {
        return 'Det finns en kommentar angående dessa uppgifter';
      }

      const fileExist = this.fileExists(category);
      if (fileExist) {
        return 'Uppgifter inskickade';
      }

      return 'Uppgifter har inte skickats in';
    },
    getLabel(category) {
      return docTypeUtils.getDocTypeStr(category);
    }
  }
};
</script>

<style scoped>
.list-items {
  display: flex;
  font-weight: 400;
  font-size: 12px;
  padding: 2px 0 2px 0;
}
.list-items .text-icon {
  width: 100%;
}
.list-items :deep(.icon-wrapper) {
  flex-shrink: 0;
}

.divider {
  width: 100%;
  height: 1px;
  background-color: var(--color-light-grey);
  border: none;
  margin: 5px 0;
}
</style>
